import type { ReactNode } from 'react'
import type { CqApCompare, CqSignal, CqWifi as CqWifiData, CqWifiEvent, CqWifiMessage } from '../api/connectionQuality'
import { CQ_COLORS, CqChart, type CqChartGuide, type CqChartSeries } from './CqChart'
import './CqWifi.css'

/**
 * Sectiunea Wi-Fi a ecranului Connection Quality (faza 3): semnalul in timp,
 * cu pragurile -67 / -72 / -80 dBm ca linii, starea curenta (AP, banda, canal,
 * latime, standard, rate, retransmisii), comparatia cu celelalte aparate de pe
 * acelasi AP, mesajele si cronologia evenimentelor.
 *
 * Totul vine gata judecat de pe server (WifiSection.php, WifiDiagnosis.php).
 * Aparatul fara date Wi-Fi n-are sectiune: `wifi` e null si nu se deseneaza
 * nimic.
 */

const SIGNAL_TEXT: Record<CqSignal, string> = { good: 'good', fair: 'fair', weak: 'weak', poor: 'very weak' }

const EVENT: Record<CqWifiEvent['kind'], { label: string; icon: string }> = {
  connect: { label: 'Connected', icon: 'fas fa-link' },
  disconnect: { label: 'Disconnected', icon: 'fas fa-unlink' },
  roam: { label: 'Roamed', icon: 'fas fa-exchange-alt' },
  fail: { label: 'Connection failed', icon: 'fas fa-times-circle' },
}

function fmt(n: number | null | undefined, unit = '', digits = 0): string {
  if (n === null || n === undefined) return '-'
  const f = Math.pow(10, digits)
  return `${Math.round(n * f) / f}${unit}`
}

/** Pragurile ca linii pe graficul de semnal. */
export function signalGuides(t: CqWifiData['thresholds']): CqChartGuide[] {
  return [
    { v: t.good, label: `${t.good} good`, color: '#1b7a42' },
    { v: t.fair, label: `${t.fair} fair`, color: '#b9770e' },
    { v: t.poor, label: `${t.poor} poor`, color: '#c0392b' },
  ]
}

export function CqWifi({
  wifi,
  from,
  to,
  bucketS,
  onOpenSettings,
  tall = false,
}: {
  wifi: CqWifiData
  from: string
  to: string
  bucketS: number
  /** Deschide Agent Settings (bifa "Allow Wi-Fi diagnostics"); lipsa = omul n-are dreptul. */
  onOpenSettings?: () => void
  /** Pagina mare: grafice mai inalte. */
  tall?: boolean
}) {
  const c = wifi.current
  const common = { from, to, bucketS }
  const signal: CqChartSeries[] = [
    {
      name: 'Signal',
      color: CQ_COLORS[0],
      points: wifi.series.map((p) => ({ t: p.bucket, v: p.rssi, lo: p.rssi_min, hi: p.rssi_max })),
    },
  ]
  const rates: CqChartSeries[] = [
    { name: 'Receive', color: CQ_COLORS[0], points: wifi.series.map((p) => ({ t: p.bucket, v: p.rx_mbps })) },
    { name: 'Transmit', color: CQ_COLORS[1], points: wifi.series.map((p) => ({ t: p.bucket, v: p.tx_mbps })) },
  ]
  const retries: CqChartSeries[] = [
    { name: 'Retries', color: CQ_COLORS[3], points: wifi.series.map((p) => ({ t: p.bucket, v: p.retry_pct })) },
  ]

  return (
    <section className="cq-wifi" data-section="wifi">
      <div className="cq-wifi-head">
        <h5>
          <i className="fas fa-wifi" aria-hidden="true"></i> Wi-Fi
          {c.ssid ? <span className="cq-wifi-ssid"> · {c.ssid}</span> : null}
        </h5>
        <span className="cq-muted cq-wifi-at">
          {c.iface ? `${c.iface} · ` : ''}as of {c.at}
        </span>
      </div>

      {wifi.messages.length ? (
        <ul className="cq-wifi-messages">
          {wifi.messages.map((m) => (
            <Message key={m.code} message={m} doc={wifi.locationDoc} onOpenSettings={onOpenSettings} />
          ))}
        </ul>
      ) : null}

      <dl className="cq-wifi-facts">
        <Fact name="Access point">
          {wifi.ap ? (
            <>
              <strong>{wifi.ap.label}</strong>
              {wifi.ap.name && wifi.ap.bssid ? <span className="cq-muted"> {wifi.ap.bssid}</span> : null}
            </>
          ) : (
            <span className="cq-muted">unknown</span>
          )}
        </Fact>
        <Fact name="Signal">
          <span className={`cq-signal is-${c.signal ?? 'none'}`} data-signal={c.signal ?? ''}>
            {fmt(c.rssi_dbm, ' dBm')}
          </span>
          {c.signal ? <span className="cq-muted"> {SIGNAL_TEXT[c.signal]}</span> : null}
          {c.signal_pct !== null ? <span className="cq-muted"> · {c.signal_pct}%</span> : null}
        </Fact>
        <Fact name="Band">
          {c.band ? `${c.band} GHz` : '-'}
          {c.channel !== null ? ` · channel ${c.channel}` : ''}
          {c.width_mhz !== null ? ` · ${c.width_mhz} MHz` : ''}
        </Fact>
        <Fact name="Standard">{c.phy ?? '-'}</Fact>
        <Fact name="Rate">
          {fmt(c.rx_mbps)} / {fmt(c.tx_mbps)} Mbps <span className="cq-muted">receive / transmit</span>
        </Fact>
        <Fact name="Retries">
          <span className={wifi.window.retry_pct !== null && wifi.window.retry_pct > wifi.thresholds.retry_high_pct ? 'cq-bad' : ''}>
            {fmt(wifi.window.retry_pct ?? c.retry_pct, '%', 1)}
          </span>
          <span className="cq-muted"> last minutes</span>
        </Fact>
      </dl>

      {wifi.compare ? <ApCompareLine compare={wifi.compare} /> : null}

      <CqChart
        title="Wi-Fi signal"
        unit="dBm"
        series={signal}
        yMin={-100}
        yMax={-20}
        height={tall ? 320 : 220}
        guides={signalGuides(wifi.thresholds)}
        {...common}
      />
      <div className="cq-two">
        <CqChart title="Wi-Fi rate" unit="Mbps" series={rates} height={tall ? 280 : 200} {...common} />
        <CqChart title="Wi-Fi retries" unit="%" series={retries} height={tall ? 280 : 200} {...common} />
      </div>

      <Timeline events={wifi.events} />
    </section>
  )
}

function Fact({ name, children }: { name: string; children: ReactNode }) {
  return (
    <div className="cq-wifi-fact" data-fact={name}>
      <dt>{name}</dt>
      <dd>{children}</dd>
    </div>
  )
}

function Message({ message, doc, onOpenSettings }: { message: CqWifiMessage; doc: string; onOpenSettings?: () => void }) {
  return (
    <li className={`is-${message.level}`} data-message={message.code}>
      <i className={message.level === 'warn' ? 'fas fa-exclamation-triangle' : 'fas fa-info-circle'} aria-hidden="true"></i>{' '}
      {message.text}
      {message.code === 'bssid_unavailable' ? (
        <>
          {onOpenSettings ? (
            <button type="button" className="btn btn-link btn-sm cq-link" onClick={onOpenSettings}>
              Open Agent Settings
            </button>
          ) : null}
          <a href={doc} target="_blank" rel="noopener noreferrer" className="cq-link">
            Why?
          </a>
        </>
      ) : null}
    </li>
  )
}

/** Comparatia cu aparatele de pe acelasi AP (ApCompare.php), un rand. */
export function ApCompareLine({ compare }: { compare: CqApCompare }) {
  return (
    <div className={`cq-compare is-${compare.code}`} data-compare={compare.code}>
      <i className="fas fa-wifi" aria-hidden="true"></i> {compare.text}
    </div>
  )
}

function Timeline({ events }: { events: CqWifiEvent[] }) {
  return (
    <div className="cq-wifi-events">
      <h5>Wi-Fi events</h5>
      {events.length === 0 ? (
        <p className="cq-muted cq-wifi-none">No connects, disconnects or roams in this period.</p>
      ) : (
        <ul>
          {events.map((e, i) => {
            const kind = EVENT[e.kind] ?? EVENT.fail
            return (
              <li key={`${e.at}-${i}`} className={`is-${e.kind}`} data-event={e.kind}>
                <span className="cq-wifi-when">{e.at}</span>
                <span className="cq-wifi-kind">
                  <i className={kind.icon} aria-hidden="true"></i> {kind.label}
                </span>
                <span className="cq-wifi-detail">{detail(e)}</span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function apOf(name: string | null, bssid: string | null): string {
  return name ?? bssid ?? 'unknown AP'
}

export function detail(e: CqWifiEvent): string {
  const parts: string[] = []
  if (e.kind === 'roam') parts.push(`${apOf(e.ap_from, e.bssid_from)} → ${apOf(e.ap_to, e.bssid_to)}`)
  else if (e.kind === 'connect') parts.push(apOf(e.ap_to, e.bssid_to))
  else if (e.bssid_from || e.ap_from) parts.push(apOf(e.ap_from, e.bssid_from))
  if (e.ssid) parts.push(e.ssid)
  if (e.reason) parts.push(e.reason)
  if (e.rssi_dbm !== null) parts.push(`${fmt(e.rssi_dbm)} dBm`)
  return parts.join(' · ')
}
